import { useEffect } from 'react';
import { MarketingHeader } from '../components/MarketingHeader';
import { MarketingFooter } from '../components/MarketingFooter';
import { SeoHead } from '../components/SeoHead';
import { Sprout, TrendingDown, Landmark, Tractor, CloudRain, Check, ArrowRight, Beef } from 'lucide-react';

interface AgriculturePageProps {
  onNavigate: (path: string) => void;
  onSignIn: () => void;
  onSignUp: () => void;
}

export function AgriculturePage({ onNavigate, onSignIn, onSignUp }: AgriculturePageProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SeoHead
        title="Gestion de Trésorerie Agricole - TrezoOne Agriculture"
        description="Pilotez votre campagne agricole du semis à l'encaissement. Anticipez votre trésorerie, gérez la volatilité des prix et les subventions PAC."
        canonical="/gestion-tresorerie-agriculteur"
        keywords="gestion trésorerie agricole, pilotage financier exploitation agricole, prévisions campagne agricole, aides PAC, volatilité prix agricoles"
      />
      <div className="min-h-screen bg-gray-50">
        <MarketingHeader onNavigate={onNavigate} onSignIn={onSignIn} onSignUp={onSignUp} />

      <main className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <div className="inline-block bg-orange-100 text-orange-800 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            En développement
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Gestion de trésorerie agricole
          </h1>
          <p className="text-2xl text-gray-700 mb-4 font-medium">
            Anticipez votre campagne. Décidez plus tôt. Dormez mieux.
          </p>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Un outil conçu pour piloter la rentabilité, la trésorerie et les risques d'une campagne agricole, avant même la récolte.
          </p>
          <button
            onClick={onSignUp}
            className="bg-green-700 text-white px-8 py-4 rounded-lg hover:bg-green-800 transition-colors font-semibold text-lg inline-flex items-center gap-2"
          >
            Découvrir TrezoOne Agriculture
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>

        {/* Section 1 - Module Cycle cultural */}
        <section className="mb-20">
          <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-green-100 rounded-lg">
                <Sprout className="h-8 w-8 text-green-700" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900">
                  Pilotez votre campagne du semis à l'encaissement
                </h2>
                <p className="text-lg text-gray-600 mt-2">
                  "Ma campagne 2026 crée-t-elle de la marge… et à quel moment est-ce que je passe en négatif ?"
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mt-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Structure de campagne</h3>
                <div className="bg-gray-50 rounded-lg p-6 space-y-3">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-gray-600 font-medium">Nom</p>
                      <p className="text-gray-900">Blé 2026</p>
                    </div>
                    <div>
                      <p className="text-gray-600 font-medium">Culture</p>
                      <p className="text-gray-900">Blé tendre</p>
                    </div>
                    <div>
                      <p className="text-gray-600 font-medium">Surface</p>
                      <p className="text-gray-900">45 hectares</p>
                    </div>
                    <div>
                      <p className="text-gray-600 font-medium">Rendement</p>
                      <p className="text-gray-900">70 qx/ha</p>
                    </div>
                    <div>
                      <p className="text-gray-600 font-medium">Prix cible</p>
                      <p className="text-gray-900">220 €/t</p>
                    </div>
                    <div>
                      <p className="text-gray-600 font-medium">Date récolte</p>
                      <p className="text-gray-900">Juillet 2026</p>
                    </div>
                  </div>
                </div>

                <h3 className="text-xl font-semibold text-gray-900 mb-4 mt-6">Charges campagne</h3>
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="space-y-2 text-sm">
                    {[
                      { label: 'Semences', amount: '4 500 €', date: 'Oct 2025' },
                      { label: 'Engrais', amount: '12 000 €', date: 'Mars 2026' },
                      { label: 'Phytosanitaire', amount: '6 800 €', date: 'Avr 2026' },
                      { label: 'Gasoil', amount: '3 200 €', date: 'Étalé' },
                      { label: 'Fermage', amount: '5 400 €', date: 'Nov 2025' }
                    ].map((charge, idx) => (
                      <div key={idx} className="flex justify-between items-center py-2 border-b border-gray-200 last:border-0">
                        <span className="text-gray-900 font-medium">{charge.label}</span>
                        <div className="text-right">
                          <span className="text-gray-900 font-semibold block">{charge.amount}</span>
                          <span className="text-gray-500 text-xs">{charge.date}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Calculs automatiques</h3>
                <div className="bg-green-50 rounded-lg p-6 space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Production prévisionnelle</p>
                    <p className="text-2xl font-bold text-gray-900">315 tonnes</p>
                    <p className="text-xs text-gray-500 mt-1">45 ha × 70 qx/ha</p>
                  </div>
                  <div className="border-t border-green-200 pt-4">
                    <p className="text-sm text-gray-600 mb-1">Chiffre d'affaires prévisionnel</p>
                    <p className="text-2xl font-bold text-gray-900">69 300 €</p>
                    <p className="text-xs text-gray-500 mt-1">315 t × 220 €/t</p>
                  </div>
                  <div className="border-t border-green-200 pt-4">
                    <p className="text-sm text-gray-600 mb-1">Marge brute prévisionnelle</p>
                    <p className="text-2xl font-bold text-green-700">37 400 €</p>
                    <p className="text-xs text-gray-500 mt-1">CA - charges variables</p>
                  </div>
                </div>

                <div className="bg-orange-50 rounded-lg p-6 mt-6 border-l-4 border-orange-500">
                  <h4 className="font-semibold text-gray-900 mb-3">Point bas de trésorerie</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-700">Mois critique</span>
                      <span className="font-semibold text-gray-900">Juillet 2026</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Trésorerie minimale</span>
                      <span className="font-bold text-orange-700">-22 000 €</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Besoin financement max</span>
                      <span className="font-semibold text-gray-900">25 000 €</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 text-center">
              <p className="text-lg font-semibold text-green-800 italic">
                La campagne devient lisible avant même d'être récoltée.
              </p>
            </div>
          </div>
        </section>

        {/* Section 2 - Module Volatilité des prix */}
        <section className="mb-20">
          <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-blue-100 rounded-lg">
                <TrendingDown className="h-8 w-8 text-blue-700" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900">
                  Piloter sous incertitude, sans spéculer
                </h2>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mt-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Simulation de variation de prix</h3>
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="mb-6">
                    <label className="text-sm text-gray-600 mb-2 block">Variation de prix</label>
                    <input
                      type="range"
                      min="-20"
                      max="20"
                      defaultValue="0"
                      className="w-full"
                      disabled
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>-20%</span>
                      <span className="font-semibold text-gray-900">Prix de base: 220 €/t</span>
                      <span>+20%</span>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-white rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-1">Prix à -10%</p>
                      <p className="text-xl font-bold text-red-700">198 €/t</p>
                      <p className="text-sm text-gray-500 mt-2">CA: 62 370 €</p>
                      <p className="text-sm text-gray-500">Marge: 30 470 €</p>
                    </div>
                    <div className="bg-white rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-1">Prix de base</p>
                      <p className="text-xl font-bold text-gray-900">220 €/t</p>
                      <p className="text-sm text-gray-500 mt-2">CA: 69 300 €</p>
                      <p className="text-sm text-gray-500">Marge: 37 400 €</p>
                    </div>
                    <div className="bg-white rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-1">Prix à +10%</p>
                      <p className="text-xl font-bold text-green-700">242 €/t</p>
                      <p className="text-sm text-gray-500 mt-2">CA: 76 230 €</p>
                      <p className="text-sm text-gray-500">Marge: 44 330 €</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Indicateurs de risque</h3>
                <div className="space-y-4">
                  <div className="bg-red-50 rounded-lg p-6 border-l-4 border-red-500">
                    <h4 className="font-semibold text-gray-900 mb-2">Seuil de rentabilité</h4>
                    <p className="text-3xl font-bold text-red-700 mb-2">182 €/t</p>
                    <p className="text-sm text-gray-600">Prix minimum pour couvrir les charges</p>
                  </div>

                  <div className="bg-orange-50 rounded-lg p-6 border-l-4 border-orange-500">
                    <h4 className="font-semibold text-gray-900 mb-2">Seuil de rendement</h4>
                    <p className="text-3xl font-bold text-orange-700 mb-2">54 qx/ha</p>
                    <p className="text-sm text-gray-600">Rendement minimum au prix cible</p>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Sensibilité au prix</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-700">-5% de prix</span>
                        <span className="font-semibold text-red-700">-3 465 € de marge</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">-10% de prix</span>
                        <span className="font-semibold text-red-700">-6 930 € de marge</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">+5% de prix</span>
                        <span className="font-semibold text-green-700">+3 465 € de marge</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 text-center">
              <p className="text-lg font-semibold text-blue-800 italic">
                Ce n'est pas de la finance de trader.<br />
                C'est de la gestion du risque.
              </p>
            </div>
          </div>
        </section>

        {/* Section 3 - Module Subventions & PAC */}
        <section className="mb-20">
          <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <Landmark className="h-8 w-8 text-emerald-700" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900">
                  Anticipez les décalages d'aides
                </h2>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mt-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Aides attendues</h3>
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="space-y-4">
                    {[
                      { type: 'PAC - Droits de base', amount: '12 800 €', theorique: 'Oct 2026', prudent: 'Déc 2026', status: 'Attendu' },
                      { type: 'MAEC Bio', amount: '3 600 €', theorique: 'Sept 2026', prudent: 'Nov 2026', status: 'Attendu' },
                      { type: 'Aide Région', amount: '2 400 €', theorique: 'Juin 2026', prudent: 'Août 2026', status: 'En cours' }
                    ].map((aide, idx) => (
                      <div key={idx} className="bg-white rounded-lg p-4 border border-gray-200">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-semibold text-gray-900 text-sm">{aide.type}</h4>
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">{aide.status}</span>
                        </div>
                        <p className="text-xl font-bold text-gray-900 mb-3">{aide.amount}</p>
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Date théorique</span>
                            <span className="text-gray-900">{aide.theorique}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Date prudente</span>
                            <span className="font-semibold text-orange-700">{aide.prudent}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Impact sur la trésorerie</h3>
                <div className="space-y-4">
                  <div className="bg-green-50 rounded-lg p-6 border-l-4 border-green-500">
                    <h4 className="font-semibold text-gray-900 mb-3">Scénario optimiste</h4>
                    <p className="text-sm text-gray-600 mb-2">Versements aux dates théoriques</p>
                    <p className="text-sm text-gray-700">Trésorerie minimale: <span className="font-semibold text-green-700">-8 200 €</span></p>
                    <p className="text-sm text-gray-700">Point bas: <span className="font-semibold">Août 2026</span></p>
                  </div>

                  <div className="bg-orange-50 rounded-lg p-6 border-l-4 border-orange-500">
                    <h4 className="font-semibold text-gray-900 mb-3">Scénario prudent</h4>
                    <p className="text-sm text-gray-600 mb-2">Versements aux dates prudentes (+2 mois)</p>
                    <p className="text-sm text-gray-700">Trésorerie minimale: <span className="font-semibold text-orange-700">-27 000 €</span></p>
                    <p className="text-sm text-gray-700">Point bas: <span className="font-semibold">Octobre 2026</span></p>
                  </div>

                  <div className="bg-red-50 rounded-lg p-6">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                        !
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Alerte anticipée</h4>
                        <p className="text-sm text-gray-700">
                          Sans versement PAC avant octobre, votre trésorerie devient négative en août.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 text-center">
              <p className="text-lg font-semibold text-emerald-800 italic">
                Les aides ne sont plus une surprise, mais un paramètre anticipé.
              </p>
            </div>
          </div>
        </section>

        {/* Section 4 - Module Matériel */}
        <section className="mb-20">
          <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-amber-100 rounded-lg">
                <Tractor className="h-8 w-8 text-amber-700" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900">
                  Investir sans fragiliser la structure
                </h2>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mt-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Projet d'investissement</h3>
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-semibold text-gray-900 mb-4">Tracteur 180 CV</h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600">Prix d'achat</span>
                      <span className="font-semibold text-gray-900">85 000 €</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600">Apport</span>
                      <span className="font-semibold text-gray-900">25 000 €</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600">Montant financé</span>
                      <span className="font-semibold text-gray-900">60 000 €</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600">Durée</span>
                      <span className="font-semibold text-gray-900">7 ans</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600">Mensualité</span>
                      <span className="font-bold text-red-700">820 €</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600">Entretien annuel estimé</span>
                      <span className="font-semibold text-gray-900">3 200 €</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-gray-600">Valeur de revente (7 ans)</span>
                      <span className="font-semibold text-gray-900">35 000 €</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Indicateurs stratégiques</h3>
                <div className="space-y-4">
                  <div className="bg-red-50 rounded-lg p-6 border-l-4 border-red-500">
                    <h4 className="font-semibold text-gray-900 mb-2">Impact mensuel sur trésorerie</h4>
                    <p className="text-3xl font-bold text-red-700 mb-2">-820 €</p>
                    <p className="text-sm text-gray-600">+ 267 €/mois d'entretien moyen</p>
                    <p className="text-sm font-semibold text-gray-700 mt-2">Charge totale: -1 087 €/mois</p>
                  </div>

                  <div className="bg-orange-50 rounded-lg p-6 border-l-4 border-orange-500">
                    <h4 className="font-semibold text-gray-900 mb-2">Impact sur seuil de rentabilité</h4>
                    <p className="text-sm text-gray-600 mb-2">Seuil avant investissement: 182 €/t</p>
                    <p className="text-sm text-gray-600 mb-2">Seuil après investissement: <span className="font-bold text-orange-700">196 €/t</span></p>
                    <p className="text-sm font-semibold text-gray-700">+14 €/t nécessaires</p>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Ratio équipement / marge</h4>
                    <p className="text-sm text-gray-600 mb-2">Marge brute annuelle moyenne: 48 000 €</p>
                    <p className="text-sm text-gray-600 mb-2">Coût annuel équipement: 13 044 €</p>
                    <p className="text-sm font-semibold text-blue-700">Ratio: 27% de la marge</p>
                  </div>

                  <div className="bg-green-50 rounded-lg p-6">
                    <h4 className="font-semibold text-gray-900 mb-2">Retour sur investissement</h4>
                    <p className="text-sm text-gray-600 mb-2">Économie prestation / an: 8 500 €</p>
                    <p className="text-sm text-gray-600 mb-2">Coût net / an: 4 544 €</p>
                    <p className="text-sm font-semibold text-green-700">ROI positif dès l'année 3</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 text-center">
              <p className="text-lg font-semibold text-amber-800 italic">
                Un tracteur est un choix stratégique, pas seulement une charge.
              </p>
            </div>
          </div>
        </section>

        {/* Section 5 - Module Élevage */}
        <section className="mb-20">
          <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-rose-100 rounded-lg">
                <Beef className="h-8 w-8 text-rose-700" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900">
                  Gestion de l'élevage
                </h2>
                <p className="text-lg text-gray-600 mt-2">
                  "Combien me coûte réellement mon troupeau... et quand est-ce rentable ?"
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mt-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Troupeau et production</h3>
                <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600 font-medium">Type d'élevage</p>
                      <p className="text-gray-900 font-semibold">Bovin viande</p>
                    </div>
                    <div>
                      <p className="text-gray-600 font-medium">Effectif total</p>
                      <p className="text-gray-900 font-semibold">85 têtes</p>
                    </div>
                    <div>
                      <p className="text-gray-600 font-medium">Vaches mères</p>
                      <p className="text-gray-900">45 unités</p>
                    </div>
                    <div>
                      <p className="text-gray-600 font-medium">Génisses</p>
                      <p className="text-gray-900">20 unités</p>
                    </div>
                    <div>
                      <p className="text-gray-600 font-medium">Veaux</p>
                      <p className="text-gray-900">20 unités</p>
                    </div>
                    <div>
                      <p className="text-gray-600 font-medium">Taux de natalité</p>
                      <p className="text-gray-900">92%</p>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Ventes prévisionnelles 2026</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-700">18 broutards (Avril)</span>
                        <span className="font-semibold text-gray-900">27 000 €</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-700">12 veaux finis (Octobre)</span>
                        <span className="font-semibold text-gray-900">21 600 €</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-gray-200">
                        <span className="text-gray-700">5 réformes (Novembre)</span>
                        <span className="font-semibold text-gray-900">6 500 €</span>
                      </div>
                      <div className="flex justify-between py-2 bg-green-50 px-2 rounded">
                        <span className="text-gray-900 font-semibold">Total ventes</span>
                        <span className="font-bold text-green-700">55 100 €</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Charges annuelles</h3>
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="space-y-2 text-sm mb-4">
                    {[
                      { label: 'Aliment concentré', amount: '14 200 €', detail: '420 €/mois' },
                      { label: 'Fourrage & paille', amount: '8 500 €', detail: 'stock annuel' },
                      { label: 'Vétérinaire & santé', amount: '4 800 €', detail: '56 €/tête/an' },
                      { label: 'Insémination', amount: '2 100 €', detail: '45 doses' },
                      { label: 'Frais d\'élevage divers', amount: '3 200 €', detail: 'matériel, litière' }
                    ].map((charge, idx) => (
                      <div key={idx} className="flex justify-between items-center py-2 border-b border-gray-200 last:border-0">
                        <div>
                          <span className="text-gray-900 font-medium block">{charge.label}</span>
                          <span className="text-gray-500 text-xs">{charge.detail}</span>
                        </div>
                        <span className="text-gray-900 font-semibold">{charge.amount}</span>
                      </div>
                    ))}
                    <div className="flex justify-between py-2 bg-red-50 px-2 rounded mt-3">
                      <span className="text-gray-900 font-semibold">Total charges</span>
                      <span className="font-bold text-red-700">32 800 €</span>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 rounded-lg p-6 mt-6 border-l-4 border-green-500">
                  <h4 className="font-semibold text-gray-900 mb-3">Rentabilité de l'atelier</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-700">Chiffre d'affaires</span>
                      <span className="font-semibold text-gray-900">55 100 €</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Charges directes</span>
                      <span className="font-semibold text-gray-900">-32 800 €</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-green-200">
                      <span className="text-gray-900 font-semibold">Marge brute</span>
                      <span className="font-bold text-green-700 text-xl">22 300 €</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Marge / vache mère</span>
                      <span className="font-semibold text-green-700">496 €</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Flux de trésorerie élevage</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-orange-50 rounded-lg p-6 border-l-4 border-orange-500">
                  <h4 className="font-semibold text-gray-900 mb-2">Période critique</h4>
                  <p className="text-sm text-gray-600 mb-2">Charges maximales</p>
                  <p className="text-2xl font-bold text-orange-700">Janv-Mars</p>
                  <p className="text-sm text-gray-500 mt-2">Concentration des achats aliment & fourrage</p>
                </div>

                <div className="bg-blue-50 rounded-lg p-6">
                  <h4 className="font-semibold text-gray-900 mb-2">Pic de charges</h4>
                  <p className="text-sm text-gray-600 mb-2">Décaissement mensuel max</p>
                  <p className="text-2xl font-bold text-blue-700">-2 800 €</p>
                  <p className="text-sm text-gray-500 mt-2">Février 2026</p>
                </div>

                <div className="bg-green-50 rounded-lg p-6">
                  <h4 className="font-semibold text-gray-900 mb-2">Recettes principales</h4>
                  <p className="text-sm text-gray-600 mb-2">Ventes broutards</p>
                  <p className="text-2xl font-bold text-green-700">+27 000 €</p>
                  <p className="text-sm text-gray-500 mt-2">Avril 2026</p>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-slate-50 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Indicateurs de pilotage</h3>
              <div className="grid md:grid-cols-4 gap-6">
                <div>
                  <p className="text-sm text-gray-600 mb-1">GMQ moyen</p>
                  <p className="text-2xl font-bold text-gray-900">1.2 kg/j</p>
                  <p className="text-xs text-gray-500 mt-1">Gain Moyen Quotidien</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Coût alimentaire</p>
                  <p className="text-2xl font-bold text-gray-900">386 €</p>
                  <p className="text-xs text-gray-500 mt-1">par tête / an</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Prix vente moyen</p>
                  <p className="text-2xl font-bold text-gray-900">1 575 €</p>
                  <p className="text-xs text-gray-500 mt-1">par broutard</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Taux rotation</p>
                  <p className="text-2xl font-bold text-gray-900">0.8</p>
                  <p className="text-xs text-gray-500 mt-1">ventes / effectif</p>
                </div>
              </div>
            </div>

            <div className="mt-8 text-center">
              <p className="text-lg font-semibold text-rose-800 italic">
                L'élevage n'est plus une boîte noire. Chaque tête a sa rentabilité.
              </p>
            </div>
          </div>
        </section>

        {/* Section 6 - Module Résilience climatique */}
        <section className="mb-20">
          <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-slate-100 rounded-lg">
                <CloudRain className="h-8 w-8 text-slate-700" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900">
                  Simuler l'imprévisible
                </h2>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mt-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Simulations climatiques</h3>
                <div className="space-y-4">
                  <div className="bg-green-50 rounded-lg p-6 border-l-4 border-green-500">
                    <h4 className="font-semibold text-gray-900 mb-3">Scénario nominal</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Rendement</span>
                        <span className="font-semibold text-gray-900">70 qx/ha</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Production</span>
                        <span className="font-semibold text-gray-900">315 tonnes</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">CA prévisionnel</span>
                        <span className="font-semibold text-gray-900">69 300 €</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Marge brute</span>
                        <span className="font-bold text-green-700">37 400 €</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-orange-50 rounded-lg p-6 border-l-4 border-orange-500">
                    <h4 className="font-semibold text-gray-900 mb-3">Scénario -20% rendement</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Rendement</span>
                        <span className="font-semibold text-gray-900">56 qx/ha</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Production</span>
                        <span className="font-semibold text-gray-900">252 tonnes</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">CA prévisionnel</span>
                        <span className="font-semibold text-orange-700">55 440 €</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Marge brute</span>
                        <span className="font-bold text-orange-700">23 540 €</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-orange-200">
                        <span className="text-gray-600">Impact trésorerie</span>
                        <span className="font-bold text-orange-700">-13 860 €</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-red-50 rounded-lg p-6 border-l-4 border-red-500">
                    <h4 className="font-semibold text-gray-900 mb-3">Scénario -40% rendement</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Rendement</span>
                        <span className="font-semibold text-gray-900">42 qx/ha</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Production</span>
                        <span className="font-semibold text-gray-900">189 tonnes</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">CA prévisionnel</span>
                        <span className="font-semibold text-red-700">41 580 €</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Marge brute</span>
                        <span className="font-bold text-red-700">9 680 €</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-red-200">
                        <span className="text-gray-600">Impact trésorerie</span>
                        <span className="font-bold text-red-700">-27 720 €</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Couverture assurantielle</h3>
                <div className="space-y-4">
                  <div className="bg-blue-50 rounded-lg p-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Assurance récolte</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Franchise</span>
                        <span className="font-semibold text-gray-900">30%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Indemnisation si -40%</span>
                        <span className="font-semibold text-blue-700">6 930 €</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Délai moyen versement</span>
                        <span className="font-semibold text-orange-700">4-6 mois</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-50 rounded-lg p-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Capacité de remboursement</h4>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Scénario nominal</p>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-green-600 h-2 rounded-full" style={{ width: '85%' }}></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Capacité excellente</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Scénario -20%</p>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-orange-500 h-2 rounded-full" style={{ width: '55%' }}></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Capacité moyenne</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Scénario -40%</p>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-red-600 h-2 rounded-full" style={{ width: '25%' }}></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Capacité fragilisée</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-50 rounded-lg p-6 border-l-4 border-slate-500">
                    <h4 className="font-semibold text-gray-900 mb-3">Impact sur trésorerie</h4>
                    <p className="text-sm text-gray-600 mb-3">Scénario -20% rendement:</p>
                    <ul className="space-y-1 text-sm text-gray-700">
                      <li className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-slate-600 flex-shrink-0 mt-0.5" />
                        <span>Trésorerie négative: 3 mois</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-slate-600 flex-shrink-0 mt-0.5" />
                        <span>Besoin financement: 32 000 €</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-slate-600 flex-shrink-0 mt-0.5" />
                        <span>Retour positif: après indemnisation</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 text-center">
              <p className="text-lg font-semibold text-slate-800 italic">
                On ne dramatise pas.<br />
                On rend visible.
              </p>
            </div>
          </div>
        </section>

        {/* Section UX Dashboard */}
        <section className="mb-20">
          <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl shadow-lg p-8 md:p-12 border-2 border-green-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Ce que voit l'agriculteur
            </h2>

            <div className="space-y-6 max-w-5xl mx-auto">
              {/* Dashboard Cultures */}
              <div className="bg-white rounded-xl p-8 shadow-md">
                <div className="border-b border-gray-200 pb-4 mb-6">
                  <h3 className="text-2xl font-bold text-gray-900">Campagne 2026 – Blé</h3>
                  <p className="text-sm text-gray-500 mt-1">Dernière mise à jour: Aujourd'hui</p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-green-50 rounded-lg p-6">
                    <p className="text-sm text-gray-600 mb-1">Marge prévisionnelle</p>
                    <p className="text-4xl font-bold text-green-700">48 000 €</p>
                  </div>

                  <div className="bg-orange-50 rounded-lg p-6 border-l-4 border-orange-500">
                    <p className="text-sm text-gray-600 mb-1">Point bas trésorerie</p>
                    <p className="text-4xl font-bold text-orange-700">-22 000 €</p>
                    <p className="text-sm text-gray-500 mt-2">en juillet 2026</p>
                  </div>

                  <div className="bg-red-50 rounded-lg p-6 border-l-4 border-red-500">
                    <p className="text-sm text-gray-600 mb-1">Seuil prix rentabilité</p>
                    <p className="text-4xl font-bold text-red-700">182 €/t</p>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-6">
                    <p className="text-sm text-gray-600 mb-1">Sensibilité -10%</p>
                    <p className="text-4xl font-bold text-blue-700">-12 400 €</p>
                  </div>

                  <div className="bg-slate-50 rounded-lg p-6 md:col-span-2 border-l-4 border-slate-500">
                    <p className="text-sm text-gray-600 mb-1">Résilience climatique -20%</p>
                    <p className="text-2xl font-bold text-slate-700">Trésorerie négative 3 mois</p>
                    <p className="text-sm text-gray-500 mt-2">Besoin financement supplémentaire: 10 000 €</p>
                  </div>
                </div>
              </div>

              {/* Dashboard Élevage */}
              <div className="bg-white rounded-xl p-8 shadow-md">
                <div className="border-b border-gray-200 pb-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-rose-100 rounded-lg">
                      <Beef className="h-6 w-6 text-rose-700" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">Atelier Élevage – Bovin viande</h3>
                      <p className="text-sm text-gray-500 mt-1">85 têtes • Dernière mise à jour: Aujourd'hui</p>
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-rose-50 rounded-lg p-6 border-l-4 border-rose-500">
                    <p className="text-sm text-gray-600 mb-1">Marge brute atelier</p>
                    <p className="text-3xl font-bold text-rose-700">22 300 €</p>
                    <p className="text-sm text-gray-500 mt-2">496 € / vache mère</p>
                  </div>

                  <div className="bg-orange-50 rounded-lg p-6">
                    <p className="text-sm text-gray-600 mb-1">Période critique</p>
                    <p className="text-3xl font-bold text-orange-700">Janv-Mars</p>
                    <p className="text-sm text-gray-500 mt-2">Pic charges: -2 800 €/mois</p>
                  </div>

                  <div className="bg-green-50 rounded-lg p-6">
                    <p className="text-sm text-gray-600 mb-1">Ventes broutards</p>
                    <p className="text-3xl font-bold text-green-700">27 000 €</p>
                    <p className="text-sm text-gray-500 mt-2">Avril 2026</p>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-6">
                    <p className="text-sm text-gray-600 mb-1">GMQ moyen</p>
                    <p className="text-3xl font-bold text-blue-700">1.2 kg/j</p>
                    <p className="text-sm text-gray-500 mt-2">Gain Moyen Quotidien</p>
                  </div>

                  <div className="bg-slate-50 rounded-lg p-6">
                    <p className="text-sm text-gray-600 mb-1">Taux de natalité</p>
                    <p className="text-3xl font-bold text-slate-700">92%</p>
                    <p className="text-sm text-gray-500 mt-2">45 vaches mères</p>
                  </div>

                  <div className="bg-amber-50 rounded-lg p-6">
                    <p className="text-sm text-gray-600 mb-1">Coût alimentaire</p>
                    <p className="text-3xl font-bold text-amber-700">386 €</p>
                    <p className="text-sm text-gray-500 mt-2">par tête / an</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 text-center space-y-2">
              <p className="text-2xl font-bold text-gray-900">Clair.</p>
              <p className="text-2xl font-bold text-gray-900">Décisionnel.</p>
              <p className="text-2xl font-bold text-gray-900">Actionnable.</p>
            </div>
          </div>
        </section>

        {/* Section finale */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-green-700 to-emerald-800 rounded-xl shadow-2xl p-12 text-white text-center">
            <h2 className="text-4xl font-bold mb-6">
              TrezoOne Agriculture
            </h2>
            <p className="text-2xl mb-8 opacity-90">
              Le tableau de bord mental du chef d'exploitation.
            </p>

            <div className="max-w-3xl mx-auto mb-8 text-left bg-white/10 rounded-lg p-8 backdrop-blur">
              <p className="text-lg leading-relaxed mb-4">
                Les agriculteurs disposent déjà d'outils comptables, bancaires et techniques.
              </p>
              <p className="text-lg leading-relaxed mb-4">
                TrezoOne ne remplace rien.
              </p>
              <p className="text-lg leading-relaxed font-semibold">
                Il apporte une vision synthétique, décisionnelle et orientée trésorerie.
              </p>
            </div>

            <button
              onClick={onSignUp}
              className="bg-white text-green-700 px-10 py-4 rounded-lg hover:bg-green-50 transition-colors font-bold text-lg inline-flex items-center gap-2 shadow-lg"
            >
              Être informé du lancement
              <ArrowRight className="h-5 w-5" />
            </button>

            <p className="text-sm mt-6 opacity-75">
              Module en développement • Lancement prévu 2026
            </p>
          </div>
        </section>
      </main>

        <MarketingFooter />
      </div>
    </>
  );
}
